import React, { useState } from 'react';
import styled from 'styled-components';
import MondayAPIService from '../../services/MondayAPIService';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, rgba(25, 30, 50, 0.98) 0%, rgba(20, 25, 45, 0.98) 100%);
  color: #fff;
  border-radius: 16px;
  border: 1px solid rgba(99, 102, 241, 0.3);
  width: min(600px, 95vw);
  max-height: 90vh;
  overflow-y: auto;
  padding: 0;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(20px);
`;

const ModalHeader = styled.div`
  padding: 28px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #fff;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: 0.3px;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.2);
    color: #fff;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
`;

const Step = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StepNumber = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: ${props => 
    props.isActive ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' : 
    props.isCompleted ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 
    'rgba(99, 102, 241, 0.15)'};
  color: ${props => props.isActive || props.isCompleted ? '#fff' : 'rgba(255, 255, 255, 0.5)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  box-shadow: ${props => props.isActive || props.isCompleted ? '0 2px 8px rgba(99, 102, 241, 0.4)' : 'none'};
`;

const StepConnector = styled.div`
  width: 40px;
  height: 2px;
  background: ${props => props.isCompleted 
    ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)' 
    : 'rgba(99, 102, 241, 0.2)'};
  margin: 0 8px;
`;

const StepTitle = styled.h3`
  color: #fff;
  margin-bottom: 16px;
  font-size: 18px;
  font-weight: 600;
`;

const Input = styled.input`
  width: 100%;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 10px;
  padding: 12px 14px;
  color: #fff;
  font-size: 14px;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    background: rgba(0, 0, 0, 0.4);
    border-color: rgba(99, 102, 241, 0.6);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
`;

const Button = styled.button`
  background-color: ${props => props.variant === 'primary' ? '#2f88f5' : 'rgba(120, 130, 140, 0.5)'};
  color: #FFFFFF;
  border: none;
  border-radius: 6px;
  padding: 12px 24px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    filter: brightness(1.1);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const HelpBox = styled.div`
  background-color: rgba(47, 136, 245, 0.1);
  border: 1px solid rgba(47, 136, 245, 0.3);
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
`;

const HelpText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 12px 0;
  font-size: 14px;
  line-height: 1.5;
`;

const HelpLink = styled.button`
  background: transparent;
  border: none;
  color: #2f88f5;
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    color: #4a9eff;
  }
`;

const BoardOption = styled.div`
  background-color: rgba(120, 130, 140, 0.08);
  border: 1px solid ${props => props.isSelected ? '#2f88f5' : 'rgba(120, 130, 140, 0.3)'};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.isSelected ? '#2f88f5' : 'rgba(120, 130, 140, 0.5)'};
    background-color: rgba(120, 130, 140, 0.12);
  }
`;

const BoardName = styled.div`
  color: #FFFFFF;
  font-weight: 600;
  margin-bottom: 4px;
`;

const BoardDescription = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
`;

const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid rgba(120, 130, 140, 0.3);
  border-top: 2px solid #2f88f5;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background-color: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  border-radius: 6px;
  padding: 12px;
  margin: 12px 0;
  color: #ff6b6b;
  font-size: 14px;
`;

const SuccessMessage = styled.div`
  background-color: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 6px;
  padding: 12px;
  margin: 12px 0;
  color: #4CAF50;
  font-size: 14px;
`;

const STEPS = [
  { id: 1, title: 'API Key', description: 'Enter your Monday.com API key' },
  { id: 2, title: 'Select Board', description: 'Choose your target board' },
  { id: 3, title: 'Confirm', description: 'Review and confirm settings' }
];

const MondayIntegrationFlow = ({ onComplete, onCancel }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [apiKey, setApiKey] = useState('');
  const [boards, setBoards] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showApiKeyHelp, setShowApiKeyHelp] = useState(false);
  const [boardPreview, setBoardPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);

  const apiClient = useAuthenticatedAxios();

  // Add global scrollbar styles
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .board-selection-scroll::-webkit-scrollbar {
        width: 8px;
      }
      .board-selection-scroll::-webkit-scrollbar-track {
        background: rgba(120, 130, 140, 0.1);
        border-radius: 4px;
      }
      .board-selection-scroll::-webkit-scrollbar-thumb {
        background: rgba(120, 130, 140, 0.6);
        border-radius: 4px;
      }
      .board-selection-scroll::-webkit-scrollbar-thumb:hover {
        background: rgba(120, 130, 140, 0.8);
      }
      .table-scroll::-webkit-scrollbar {
        width: 6px;
        height: 6px;
      }
      .table-scroll::-webkit-scrollbar-track {
        background: rgba(120, 130, 140, 0.1);
      }
      .table-scroll::-webkit-scrollbar-thumb {
        background: rgba(120, 130, 140, 0.5);
        border-radius: 3px;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const handleApiKeySubmit = async () => {
    if (!apiKey.trim()) {
      setError('Please enter your API key');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const mondayService = new MondayAPIService(apiKey.trim());
      const fetchedBoards = await mondayService.getBoards();
      
      if (fetchedBoards && fetchedBoards.length > 0) {
        setBoards(fetchedBoards);
        setCurrentStep(2);
      } else {
        setError('No boards found. Please check if your API key has the correct permissions.');
      }
    } catch (err) {
      console.error('Error fetching boards:', err);
      setError('Failed to connect to Monday.com. Please check your API key and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBoardSelect = (board) => {
    setSelectedBoard(board);
    // Clear any existing preview when selecting a different board
    if (boardPreview && boardPreview.id !== board.id) {
      setBoardPreview(null);
      setShowPreview(false);
    }
  };

  const handlePreviewBoard = async (board) => {
    if (!apiKey.trim()) return;

    setPreviewLoading(true);
    setError('');

    try {
      const mondayService = new MondayAPIService(apiKey.trim());
      const preview = await mondayService.getBoardPreview(board.id, 5); // Get 5 sample items
      setBoardPreview(preview);
      setShowPreview(true);
    } catch (err) {
      console.error('Error fetching board preview:', err);
      setError('Failed to load board preview. Please try again.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedBoard) {
      setError('Please select a board');
      return;
    }

    setSaving(true);
    setError('');

    try {
      // Save to backend API
      await apiClient.post('/profile/crm', {
        key: apiKey,
        board_id: selectedBoard.id,
        type: 'Monday'
      });

      // Prepare connection data for parent component
      const connectionData = {
        apiKey,
        boardId: selectedBoard.id,
        boardName: selectedBoard.name,
        boardDescription: selectedBoard.description || ''
      };

      // Call parent's onComplete callback
      onComplete(connectionData);
    } catch (err) {
      console.error('Error saving CRM connection:', err);
      setError(err.response?.data?.message || 'Failed to save CRM connection. Please try again.');
      setSaving(false);
    }
  };

  const renderStep1 = () => (
    <div>
      <StepTitle>Connect to Monday.com</StepTitle>
      
      <div style={{ marginBottom: '16px' }}>
        <Label>Monday.com API Key</Label>
        <Input
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="Enter your Monday.com API key"
          onKeyPress={(e) => e.key === 'Enter' && handleApiKeySubmit()}
        />
      </div>

      <HelpBox>
        <HelpText>
          Need help finding your API key? 
        </HelpText>
        <HelpLink onClick={() => setShowApiKeyHelp(true)}>
          📖 Show me where to find it
        </HelpLink>
      </HelpBox>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <ButtonGroup>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleApiKeySubmit}
          disabled={loading || !apiKey.trim()}
        >
          {loading ? <LoadingSpinner /> : 'Continue'}
        </Button>
      </ButtonGroup>
    </div>
  );

  // Filter boards based on search term
  const filteredBoards = boards.filter(board => 
    board.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    board.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    board.id.toString().includes(searchTerm)
  );

  const renderStep2 = () => (
    <div>
      <StepTitle>Select Your Board</StepTitle>
      
      <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '20px' }}>
        Choose which Monday.com board to store your leads.
      </p>

      {/* Search Input */}
      <div style={{ marginBottom: '16px' }}>
        <Label>
          🔍 Search Boards ({boards.length} total{searchTerm ? ` • ${filteredBoards.length} shown` : ''})
        </Label>
        <Input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setSearchTerm('');
            }
          }}
          placeholder="Search by board name, description, or ID... (ESC to clear)"
          style={{ marginBottom: '8px' }}
        />
        {searchTerm && (
          <div style={{ 
            fontSize: '12px', 
            color: 'rgba(255, 255, 255, 0.6)',
            marginBottom: '8px' 
          }}>
            Found {filteredBoards.length} board{filteredBoards.length !== 1 ? 's' : ''} matching "<strong style={{ color: '#2f88f5' }}>{searchTerm}</strong>"
            {filteredBoards.length > 0 && searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#2f88f5',
                  cursor: 'pointer',
                  marginLeft: '8px',
                  fontSize: '12px',
                  textDecoration: 'underline'
                }}
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      <div className="board-selection-scroll" style={{ 
        maxHeight: '500px', 
        overflowY: 'auto',
        border: '1px solid rgba(120, 130, 140, 0.3)',
        borderRadius: '8px',
        backgroundColor: 'rgba(120, 130, 140, 0.05)',
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(120, 130, 140, 0.6) rgba(120, 130, 140, 0.1)'
      }}>
        {filteredBoards.map((board, index) => (
          <div key={`board-wrapper-${board.id}-${index}`}>
            <BoardOption
              isSelected={selectedBoard?.id === board.id}
              onClick={() => handleBoardSelect(board)}
              style={{
                borderColor: searchTerm && (
                  board.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  board.description?.toLowerCase().includes(searchTerm.toLowerCase())
                ) ? 'rgba(47, 136, 245, 0.4)' : undefined
              }}
            >
              <div style={{ flex: 1 }}>
                <BoardName>{board.name}</BoardName>
                <BoardDescription>
                  {board.description || `Board ID: ${board.id} • ${board.columns?.length || 0} columns`}
                </BoardDescription>
                {board.columns && board.columns.length > 0 && (
                  <div style={{ 
                    marginTop: '8px',
                    marginBottom: '12px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px'
                  }}>
                    <span style={{ 
                      fontSize: '10px', 
                      color: 'rgba(255, 255, 255, 0.6)',
                      marginRight: '6px',
                      alignSelf: 'center'
                    }}>
                      Columns:
                    </span>
                    {board.columns.slice(0, 5).map((col, idx) => (
                      <span key={`quick-${col.id}-${idx}`} style={{
                        fontSize: '10px',
                        backgroundColor: 'rgba(120, 130, 140, 0.25)',
                        color: 'rgba(255, 255, 255, 0.8)',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        border: '1px solid rgba(120, 130, 140, 0.4)'
                      }}>
                        {col.title}
                      </span>
                    ))}
                    {board.columns.length > 5 && (
                      <span style={{
                        fontSize: '10px',
                        color: 'rgba(255, 255, 255, 0.5)',
                        alignSelf: 'center'
                      }}>
                        +{board.columns.length - 5} more
                      </span>
                    )}
                  </div>
                )}
                
                {/* Preview Button - Positioned Lower */}
                <div style={{ 
                  marginTop: '8px',
                  display: 'flex',
                  justifyContent: 'flex-end'
                }}>
                  <Button
                    variant="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePreviewBoard(board);
                    }}
                    disabled={previewLoading}
                    style={{ 
                      fontSize: '12px', 
                      padding: '6px 12px',
                      minWidth: '80px'
                    }}
                  >
                    {previewLoading && boardPreview?.id === board.id ? (
                      <LoadingSpinner style={{ width: '12px', height: '12px' }} />
                    ) : (
                      '👁️ Preview'
                    )}
                  </Button>
                </div>
              </div>
            </BoardOption>
          </div>
        ))}
        
        {/* No Search Results Message */}
        {searchTerm && filteredBoards.length === 0 && (
          <div style={{
            padding: '40px 20px',
            textAlign: 'center',
            color: 'rgba(255, 255, 255, 0.6)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔍</div>
            <div style={{ fontSize: '16px', marginBottom: '8px', color: '#FFFFFF' }}>
              No boards found
            </div>
            <div style={{ fontSize: '14px', marginBottom: '16px' }}>
              No boards match "{searchTerm}"
            </div>
            <button
              onClick={() => setSearchTerm('')}
              style={{
                background: '#2f88f5',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Clear Search
            </button>
          </div>
        )}

        {/* No Boards Available Message */}
        {!searchTerm && boards.length === 0 && (
          <div style={{
            padding: '40px 20px',
            textAlign: 'center',
            color: 'rgba(255, 255, 255, 0.6)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📋</div>
            <div style={{ fontSize: '16px', marginBottom: '8px', color: '#FFFFFF' }}>
              No boards available
            </div>
            <div style={{ fontSize: '14px' }}>
              Your Monday.com account doesn't have any accessible boards.
            </div>
          </div>
        )}
      </div>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <ButtonGroup>
        <Button variant="secondary" onClick={() => setCurrentStep(1)}>
          Back
        </Button>
        <Button 
          variant="primary" 
          onClick={() => setCurrentStep(3)}
          disabled={!selectedBoard}
        >
          Continue
        </Button>
      </ButtonGroup>
    </div>
  );

  const renderStep3 = () => (
    <div>
      <StepTitle>Confirm Connection</StepTitle>
      
      <div style={{ marginBottom: '20px' }}>
        <div style={{ 
          background: 'rgba(120, 130, 140, 0.08)', 
          padding: '16px', 
          borderRadius: '8px',
          marginBottom: '16px'
        }}>
          <h4 style={{ color: '#FFFFFF', margin: '0 0 12px 0' }}>Connection Summary</h4>
          <div style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            <p><strong>Platform:</strong> Monday.com</p>
            <p><strong>Board:</strong> {selectedBoard?.name}</p>
            <p><strong>API Key:</strong> •••••{apiKey.slice(-4)}</p>
          </div>
        </div>

        <HelpBox>
          <HelpText>
            Chat leads will be added to this board automatically.
          </HelpText>
        </HelpBox>
      </div>

      {!saving && (
        <SuccessMessage>
          Ready to connect!
        </SuccessMessage>
      )}

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <ButtonGroup>
        <Button
          variant="secondary"
          onClick={() => setCurrentStep(2)}
          disabled={saving}
        >
          Back
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={saving}
        >
          {saving ? <LoadingSpinner /> : 'Complete Setup'}
        </Button>
      </ButtonGroup>
    </div>
  );

  return (
    <Modal onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Monday.com Integration</ModalTitle>
          <CloseButton onClick={onCancel}>Close</CloseButton>
        </ModalHeader>

        <ModalBody>
          {/* Step Indicator */}
          <StepIndicator>
            {STEPS.map((step, index) => (
              <Step key={step.id}>
                <StepNumber 
                  isActive={currentStep === step.id}
                  isCompleted={currentStep > step.id}
                >
                  {currentStep > step.id ? '✓' : step.id}
                </StepNumber>
                {index < STEPS.length - 1 && (
                  <StepConnector isCompleted={currentStep > step.id} />
                )}
              </Step>
            ))}
          </StepIndicator>

          {/* Step Content */}
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}

          {/* API Key Help Modal */}
          {showApiKeyHelp && (
            <div style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.8)',
              zIndex: 20000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{
                background: '#1E1E1E',
                borderRadius: '12px',
                padding: '24px',
                width: 'min(500px, 90vw)',
                maxHeight: '80vh',
                overflowY: 'auto'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0, color: '#FFFFFF' }}>How to Get Your Monday.com API Key</h3>
                  <CloseButton onClick={() => setShowApiKeyHelp(false)}>Close</CloseButton>
                </div>
                
                <div style={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.6 }}>
                  <p><strong>Step 1:</strong> Click on your name/icon (top right corner)</p>
                  <p><strong>Step 2:</strong> Click on <strong>"Develop"</strong> in the dropdown</p>
                  <p><strong>Step 3:</strong> Click on <strong>"API token"</strong></p>
                  <p><strong>Step 4:</strong> Click <strong>"Copy"</strong> button</p>
                  <p><strong>Step 5:</strong> Paste it here</p>
                  
                  <div style={{ 
                    background: 'rgba(120, 130, 140, 0.1)', 
                    border: '1px solid rgba(120, 130, 140, 0.3)', 
                    borderRadius: '6px', 
                    padding: '12px', 
                    marginTop: '16px' 
                  }}>
                    <strong>💡 Note:</strong> Keep your API key secure.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Board Preview Modal */}
          {showPreview && boardPreview && (
            <div style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.8)',
              zIndex: 20000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{
                background: '#1E1E1E',
                borderRadius: '12px',
                padding: '24px',
                width: 'min(1200px, 95vw)',
                maxHeight: '90vh',
                overflowY: 'auto',
                border: '1px solid rgba(120, 130, 140, 0.3)'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: '20px',
                  paddingBottom: '16px',
                  borderBottom: '1px solid rgba(120, 130, 140, 0.2)',
                  position: 'sticky',
                  top: '0',
                  backgroundColor: '#1E1E1E',
                  zIndex: 10
                }}>
                  <div>
                    <h3 style={{ margin: '0 0 4px 0', color: '#FFFFFF' }}>Board Preview: {boardPreview.name}</h3>
                    <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.7)' }}>
                      {boardPreview.totalColumns} columns • {boardPreview.sampleItemCount} items shown
                    </div>
                  </div>
                  <CloseButton onClick={() => setShowPreview(false)}>Close</CloseButton>
                </div>
                
                <div style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  {boardPreview.description && (
                    <div style={{ 
                      background: 'rgba(47, 136, 245, 0.1)', 
                      border: '1px solid rgba(47, 136, 245, 0.3)', 
                      borderRadius: '8px', 
                      padding: '12px', 
                      marginBottom: '20px',
                      fontSize: '14px',
                      color: 'rgba(255, 255, 255, 0.9)'
                    }}>
                      📝 <strong>Description:</strong> {boardPreview.description}
                    </div>
                  )}

                  {/* Board Preview Table */}
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ color: '#FFFFFF', marginBottom: '16px' }}>
                      Board Structure & Sample Data ({boardPreview.sampleItemCount} items total - showing first 5)
                    </h4>
                    
                    {/* Horizontal Table Layout */}
                    <div style={{ 
                      border: '1px solid rgba(120, 130, 140, 0.3)',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      backgroundColor: 'rgba(120, 130, 140, 0.05)'
                    }}>
                      {/* Table Header */}
                      <div className="table-scroll" style={{ 
                        display: 'flex',
                        backgroundColor: 'rgba(47, 136, 245, 0.15)',
                        borderBottom: '2px solid rgba(47, 136, 245, 0.3)',
                        overflowX: 'auto'
                      }}>
                        {/* Item Name Column */}
                        <div style={{
                          minWidth: '180px',
                          padding: '12px',
                          fontWeight: '600',
                          color: '#FFFFFF',
                          borderRight: '1px solid rgba(120, 130, 140, 0.3)',
                          fontSize: '14px'
                        }}>
                          Item Name
                        </div>
                        
                        {/* Column Headers */}
                        {boardPreview.columns.slice(0, 8).map((column, index) => (
                          <div key={`header-${column.id}-${index}`} style={{
                            minWidth: '140px',
                            padding: '12px 8px',
                            borderRight: '1px solid rgba(120, 130, 140, 0.3)',
                            fontSize: '12px',
                            color: '#FFFFFF'
                          }}>
                            <div style={{ fontWeight: '600', marginBottom: '2px' }}>
                              {column.title}
                            </div>
                            <div style={{ 
                              fontSize: '10px', 
                              color: 'rgba(255, 255, 255, 0.6)',
                              backgroundColor: 'rgba(120, 130, 140, 0.3)',
                              padding: '1px 4px',
                              borderRadius: '3px',
                              display: 'inline-block'
                            }}>
                              {column.type}
                            </div>
                          </div>
                        ))}
                        
                        {boardPreview.columns.length > 8 && (
                          <div style={{
                            minWidth: '60px',
                            padding: '12px 8px',
                            fontSize: '12px',
                            color: 'rgba(255, 255, 255, 0.7)',
                            textAlign: 'center'
                          }}>
                            +{boardPreview.columns.length - 8} more
                          </div>
                        )}
                      </div>

                      {/* Table Rows */}
                      <div className="table-scroll" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {boardPreview.sampleItems.slice(0, 5).map((item, itemIndex) => (
                          <div key={`row-${item.id}-${itemIndex}`} className="table-scroll" style={{
                            display: 'flex',
                            borderBottom: itemIndex < 4 ? '1px solid rgba(120, 130, 140, 0.2)' : 'none',
                            backgroundColor: itemIndex % 2 === 0 ? 'rgba(120, 130, 140, 0.02)' : 'transparent',
                            overflowX: 'auto'
                          }}>
                            {/* Item Name Cell */}
                            <div style={{
                              minWidth: '180px',
                              padding: '12px',
                              borderRight: '1px solid rgba(120, 130, 140, 0.2)',
                              color: '#FFFFFF',
                              fontSize: '13px',
                              fontWeight: '500'
                            }}>
                              <div style={{ 
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {item.name}
                              </div>
                              <div style={{ 
                                fontSize: '10px', 
                                color: 'rgba(255, 255, 255, 0.4)',
                                marginTop: '2px'
                              }}>
                                ID: {item.id}
                              </div>
                            </div>
                            
                            {/* Data Cells */}
                            {boardPreview.columns.slice(0, 8).map((column, colIndex) => {
                              const value = item.values[column.title];
                              return (
                                <div key={`cell-${item.id}-${column.id}-${colIndex}`} style={{
                                  minWidth: '140px',
                                  padding: '12px 8px',
                                  borderRight: '1px solid rgba(120, 130, 140, 0.2)',
                                  fontSize: '12px',
                                  color: 'rgba(255, 255, 255, 0.8)'
                                }}>
                                  <div style={{ 
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {value?.text || value?.value || '—'}
                                  </div>
                                </div>
                              );
                            })}
                            
                            {boardPreview.columns.length > 8 && (
                              <div style={{
                                minWidth: '60px',
                                padding: '12px 8px',
                                fontSize: '11px',
                                color: 'rgba(255, 255, 255, 0.5)',
                                textAlign: 'center'
                              }}>
                                ...
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Column Summary */}
                  {boardPreview.columns.length > 8 && (
                    <div style={{
                      background: 'rgba(47, 136, 245, 0.1)',
                      border: '1px solid rgba(47, 136, 245, 0.3)',
                      borderRadius: '6px',
                      padding: '12px',
                      marginBottom: '16px'
                    }}>
                      <div style={{ color: '#FFFFFF', fontWeight: '600', marginBottom: '8px', fontSize: '14px' }}>
                        All Columns ({boardPreview.columns.length} total)
                      </div>
                      <div className="table-scroll" style={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: '6px',
                        maxHeight: '120px',
                        overflowY: 'auto'
                      }}>
                        {boardPreview.columns.map((column, index) => (
                          <div key={`col-tag-${column.id}-${index}`} style={{
                            backgroundColor: 'rgba(120, 130, 140, 0.2)',
                            color: 'rgba(255, 255, 255, 0.9)',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            border: '1px solid rgba(120, 130, 140, 0.3)'
                          }}>
                            {column.title} <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>({column.type})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {boardPreview.sampleItems.length === 0 && (
                    <div style={{
                      background: 'rgba(255, 193, 7, 0.1)',
                      border: '1px solid rgba(255, 193, 7, 0.3)',
                      borderRadius: '8px',
                      padding: '16px',
                      textAlign: 'center'
                    }}>
                      <div style={{ color: '#ffc107', marginBottom: '8px' }}>📋</div>
                      <div style={{ color: '#FFFFFF', fontWeight: '600', marginBottom: '4px' }}>
                        This board is empty
                      </div>
                      <div style={{ fontSize: '14px' }}>
                        No items found in this board. You can still use it to store your leads.
                      </div>
                    </div>
                  )}

                  <div style={{ 
                    marginTop: '20px', 
                    textAlign: 'center',
                    padding: '12px',
                    background: 'rgba(76, 175, 80, 0.1)',
                    border: '1px solid rgba(76, 175, 80, 0.3)',
                    borderRadius: '8px'
                  }}>
                    <div style={{ color: '#4CAF50', marginBottom: '8px' }}>✅</div>
                    <div style={{ fontSize: '14px' }}>
                      This board can be used for lead integration.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default MondayIntegrationFlow;
